import * as React from 'react'
import { clsx } from 'clsx'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'

import { CheckItem, Root } from '../../properties-panel'
import * as PropertiesPanel from '../../properties-panel'
import { DocumentSidebarPortal } from '../../document-sidebar-portal'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  Block,
  ComponentBlock,
  DocumentBlock,
  isCommentBlock,
  blockTypesRecord,
  isRichTextBlock,
  shouldInheritHiddenState,
  normalizeStringPath,
  isVariantSetBlock,
  isNonCrucialBlock,
  isBlockWithVariantOverrides,
  isBlockWithVariants,
} from '@basehub/data/replicache/index'
import { getId } from '@basehub/utils/id/index'
import { ChevronDownIcon, ComponentNoneIcon } from '@radix-ui/react-icons'
import { DocActionDialog } from '~/sotano-kit/primitives/confirmation-dialog'
import { TreeNode } from '@basehub/utils/tree/index'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { BlockIcon } from '~/components/block-icon'
import { ReferencedByGroup } from '../instance/properties-panel'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { validateBlockNesting } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { invariant } from '@epic-web/invariant'
import { Button } from '~/sotano-kit/primitives/button'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useEditorStore } from '../../editor-store'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { ToggleMakeComponentParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/component'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'
import { useTreeSlice } from '~/hooks/use-tree-slice'

type Props = {
  block: DocumentBlock | ComponentBlock
  path: string
  parent: Block
  strict: boolean
}

export const DocumentPropertiesPanel = ({ block, path, parent }: Props) => {
  const [removeComponentToggleState, setRemoveComponentToggleState] =
    React.useState(false)
  const [seeAllInstances, setSeeAllInstances] = React.useState(false)
  const [seeAllRichTextInstances, setSeeAllRichTextInstances] =
    React.useState(false)
  const [seeAllCollections, setSeeAllCollections] = React.useState(false)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { data } = useRepoReplicache()
  const undoManager = useRepoUndoManager()
  const editorMode = useEditorMode()
  const { encodedSegment } = useBranchOrCommitSegment()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const replicache = data?.replicache
  const isComponent = isComponentBlock(block)

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: block.id,
    includeEdge: false,
  })
  const parentId = normalizedPath[normalizedPath.length - 1]

  const workingTree = useWorkingTreeStore((s) => s.tree)
  const parentNode = parentId ? workingTree?.getNode(parentId) : null

  const handleRemoveComponent = React.useCallback(async () => {
    if (!replicache) throw new Error('No replicache')
    await replicache.mutate.toggleMakeComponent({
      value: false,
      blockId: block.id,
      path,
      idSeed: getId(),
      isoNow: new Date().toISOString(),
    })
  }, [block.id, path, replicache])

  const isList = parentNode?.value ? isListBlock(parentNode?.value) : false
  const isInstance = parentNode?.value
    ? isInstanceBlock(parentNode?.value)
    : false

  const blockTreeManager = useWorkingTreeStore((s) => s.tree)

  const relations = React.useMemo(() => {
    if (!isComponent || !blockTreeManager) return null
    const instances: TreeNode<Block>[] = []
    const instancesInRichText: TreeNode<Block>[] = []
    const collections: TreeNode<Block>[] = []

    for (const node of blockTreeManager.itemsMap.values()) {
      if (
        isInstanceBlock(node.value) &&
        node.value.typeOptions.targetBlockId === sourceBlockId
      ) {
        invariant(
          node.parentKey,
          `Instance block ${node.value.id} has no parent key`
        )
        const parent = blockTreeManager.getNode(node.parentKey)
        if (parent && !isRichTextBlock(parent?.value)) {
          const copy = { ...node }
          instances.push(copy)
        }
      } else if (
        isListBlock(node.value) &&
        node.value.typeOptions.targetBlockId === sourceBlockId
      ) {
        const copy = { ...node }
        collections.push(copy)
      } else if (isRichTextBlock(node.value) && node.value.value) {
        blockTreeManager.getChildrenKeys(node.key).forEach((cId) => {
          const node = blockTreeManager.getNode(cId)
          if (
            !node ||
            !isInstanceBlock(node.value) ||
            node.value.typeOptions.targetBlockId !== sourceBlockId
          ) {
            return
          }
          const copy = { ...node }
          instancesInRichText.push(copy)
        })
      }
    }

    return { instances, collections, instancesInRichText }
  }, [sourceBlockId, blockTreeManager, isComponent])

  const instanceCount = relations?.instances.length ?? 0
  const collectionsCount = relations?.collections.length ?? 0
  const instancesInRichTextCount = relations?.instancesInRichText.length ?? 0

  // check if the block can be detached in its nested path
  const detached =
    block.type === blockTypesRecord.document
      ? ({
          ...block,
          type: blockTypesRecord.component,
          typeOptions: {
            displayTitle: '',
            displayDescription: '',
            displayIcon: blockTypesRecord.component,
          },
        } as ComponentBlock)
      : { ...block, type: blockTypesRecord.document }

  const toggleDocumentTypeCheck = validateBlockNesting({
    block: detached,
    path,
    shouldThrowIfInvalid: false,
    fetchBlock: (id) => blockTreeManager?.getNode(id)?.value,
    fetchChildren: (id) => {
      const keys = blockTreeManager?.getChildrenKeys(id)

      return (
        keys?.map((k) => blockTreeManager?.getNode(k)?.value).filter(Boolean) ??
        []
      )
    },
  })

  const [setsInUse, variantSetsOptions] = React.useMemo(() => {
    const _variantSetsOptions: PropertiesPanel.CheckListOption[] = []
    const _setsInUse: PropertiesPanel.CheckListOption[] = []
    if (!blockTreeManager) return [_setsInUse, _variantSetsOptions]
    for (const cId of (
      blockTreeManager.compoundIndexes.get('blocksByType') as Map<
        Block['type'],
        Set<string>
      >
    )?.get('variant-set') ?? new Set()) {
      const node = blockTreeManager?.getNode(cId)
      if (!node) continue
      if (!isVariantSetBlock(node.value)) continue
      invariant(node.parentKey, 'Variant set has no parent key')

      const option = {
        label: node.value.title || 'Untitled',
        value: idToSourceBlockId(node.value),
        icon: <BlockIcon block={node.value} />,
        href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${node.parentKey}#${node.key}`,
        parentKey: node.parentKey,
      }

      if (
        block.constraints.acceptedVariantSets?.some(
          (setId) => setId === idToSourceBlockId(node.value)
        )
      ) {
        _setsInUse.push(option)
      } else {
        _variantSetsOptions.push(option)
      }
    }

    return [_setsInUse, _variantSetsOptions]
  }, [
    block.constraints.acceptedVariantSets,
    blockTreeManager,
    encodedSegment,
    repoSlug,
    teamSlug,
  ])

  const handleVariantSetSelect = React.useCallback(
    async (setId: string, enabled: boolean) => {
      let params: UpdateBlockParams
      if (!enabled) {
        params = {
          data: {
            constraints: {
              acceptedVariantSets:
                block.constraints.acceptedVariantSets?.filter(
                  (s) => s !== setId
                ) ?? null,
            },
          },
          instanceChildTarget: block.instanceChildTarget,
          path,
          isoNow: new Date().toISOString(),
          id: block.id,
          strategy: 'merge-deep',
        }
      } else {
        params = {
          data: {
            constraints: {
              acceptedVariantSets: [
                ...(block.constraints.acceptedVariantSets ?? []),
                setId,
              ],
            },
          },
          instanceChildTarget: block.instanceChildTarget,
          path,
          isoNow: new Date().toISOString(),
          id: block.id,
          strategy: 'merge-deep',
        }
      }
      if (!params) return
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        if (result?.undo) {
          undoManager.current?.push({
            redo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              { op: 'updateBlock', args: params },
            ],
            undo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              {
                op: 'blockBatchMutations',
                args: { mutations: result.undo },
              },
            ],
          })
        }
      }
    },
    [
      block.constraints.acceptedVariantSets,
      block.instanceChildTarget,
      block.id,
      getInputFocusOp,
      path,
      replicache?.mutate,
      sourceBlockId,
      undoManager,
    ]
  )

  const childrenList = useTreeSlice(
    blockTreeManager,
    (s) =>
      (s
        .getChildrenKeys(block.id)
        ?.map((key) => {
          const child = blockTreeManager?.getNode(key)?.value
          if (!child || isCommentBlock(child)) return null
          return {
            id: child.id,
            name: child.title || 'Untitled',
            icon: <BlockIcon block={child} />,
            disabled: !isBlockWithVariantOverrides(child),
            disabledReason: isListBlock(child)
              ? 'Lists variants are configured in their template component.'
              : !isBlockWithVariants(child)
                ? `${child.type} does not support variants.`
                : 'Layout blocks variants are configured in their own properties panel.',
          }
        })
        .filter(Boolean) ?? []) satisfies CheckItem[]
  )

  const getCheckedChildren = React.useCallback(
    (setId: string) =>
      childrenList?.filter((block) => {
        const child = blockTreeManager?.getNode(block.id)?.value
        return Boolean(
          child &&
            isBlockWithVariants(child) &&
            child.constraints.acceptedVariantSets?.some((key) => setId === key)
        )
      }) ?? [],
    [blockTreeManager, childrenList]
  )

  const handleVariantUpdateForChild = React.useCallback(
    async ({
      newValue,
      items: blocks,
      setId: forSetId,
    }: {
      newValue: boolean | 'overwrite'
      items: CheckItem[]
      setId: string
    }) => {
      let blocksToSupport: CheckItem[] = []
      let blocksToDisable: CheckItem[] = []
      if (newValue === 'overwrite') {
        for (const childCheck of childrenList ?? []) {
          if (childCheck.disabled) continue
          if (blocks.some(({ id }) => childCheck.id === id)) {
            blocksToSupport.push(childCheck)
          } else {
            blocksToDisable.push(childCheck)
          }
        }
      } else if (newValue === true) {
        blocksToSupport = blocks
      } else {
        blocksToDisable = blocks
      }

      for (const blockCheck of blocksToSupport) {
        const path = blockTreeManager?.getPath(blockCheck.id)
        const child = blockTreeManager?.getNode(blockCheck.id)?.value
        if (!path || !child || !isBlockWithVariants(child)) continue

        const previous = child.constraints.acceptedVariantSets ?? []
        const params: UpdateBlockParams = {
          id: child.id,
          data: {
            constraints: {
              acceptedVariantSets: [...previous, forSetId],
            },
          },
          instanceChildTarget: null,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        }
        const result = await replicache?.mutate.updateBlock(params)
        if (result?.undo) {
          undoManager.current?.push({
            redo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              { op: 'updateBlock', args: params },
            ],
            undo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              {
                op: 'blockBatchMutations',
                args: { mutations: result.undo },
              },
            ],
          })
        }
      }
      for (const blockCheck of blocksToDisable) {
        const path = blockTreeManager?.getPath(blockCheck.id)
        const child = blockTreeManager?.getNode(blockCheck.id)?.value
        if (!path || !child || !isBlockWithVariants(child)) return

        const previous = child.constraints.acceptedVariantSets ?? []
        const params: UpdateBlockParams = {
          id: child.id,
          data: {
            constraints: {
              acceptedVariantSets: previous.filter(
                (setId) => setId !== forSetId
              ),
            },
          },
          instanceChildTarget: null,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        }
        const result = await replicache?.mutate.updateBlock(params)
        if (result?.undo) {
          undoManager.current?.push({
            redo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              { op: 'updateBlock', args: params },
            ],
            undo: [
              getInputFocusOp(`el-${sourceBlockId}-pp-variants`),
              {
                op: 'blockBatchMutations',
                args: { mutations: result.undo },
              },
            ],
          })
        }
      }
    },
    [
      blockTreeManager,
      childrenList,
      getInputFocusOp,
      replicache?.mutate,
      sourceBlockId,
      undoManager,
    ]
  )

  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <HiddenTogglePropertiesPanel
          block={block}
          path={path}
          parent={parent}
        />
        <PropertiesPanel.ConfigureContainer block={block}>
          {(Boolean(editorMode === 'write:all' && variantSetsOptions?.length) ||
            !!setsInUse?.length) && (
            <PropertiesPanel.MultiCheckListsOption
              title="Variants"
              iconButtonProps={{
                id: `el-${sourceBlockId}-pp-variants`,
                tooltip: {
                  content: 'Add new variant set',
                  side: 'left',
                },
              }}
              checkListPopoverProps={{
                itemName: 'Blocks',
                prefix: 'Blocks',
                title: 'affected',
                allowedLabel: 'Affected',
                notAllowedLabel: 'Not affected',
              }}
              getItemsByCheckList={() => childrenList ?? []}
              setsInUse={setsInUse}
              availableOptionSets={variantSetsOptions ?? []}
              setOption={handleVariantSetSelect}
              handleItemsToggle={handleVariantUpdateForChild}
              getCheckedChildren={getCheckedChildren}
              emptyState="Selected sets will show up here."
            />
          )}

          {!isList && !isInstance && (
            <PropertiesPanel.Group
              /**
               * as the document has no other things in its properties panel,
               * we don't want to show the bottom border either.
               * in the future, we may need to reevaluate this.
               */
              noBorderBottom={!isComponent}
            >
              <SimpleTooltip
                disabled={
                  toggleDocumentTypeCheck.isValid && !instancesInRichTextCount
                }
                contentClassName="max-w-[300px]"
                align="end"
                delayDuration={100}
                content={
                  instancesInRichTextCount
                    ? 'This component has instances in rich text blocks. Detach them first.'
                    : !toggleDocumentTypeCheck.isValid
                      ? toggleDocumentTypeCheck.reason
                      : ''
                }
              >
                <Button
                  intent="secondary"
                  disabled={
                    !toggleDocumentTypeCheck.isValid ||
                    instancesInRichTextCount > 0
                  }
                  id={`el-${sourceBlockId}-pp-toggle-doc-comp`}
                  onClick={async () => {
                    if (!replicache) throw new Error('No replicache')
                    if (!toggleDocumentTypeCheck.isValid) return

                    if (isComponent) {
                      if (instanceCount > 0 || instancesInRichTextCount > 0) {
                        setRemoveComponentToggleState(true)
                      } else {
                        await handleRemoveComponent()
                      }
                      return
                    }

                    const params: ToggleMakeComponentParams = {
                      value: true,
                      blockId: block.id,
                      path,
                      idSeed: getId(),
                      isoNow: new Date().toISOString(),
                    }
                    const result =
                      await replicache.mutate.toggleMakeComponent(params)
                    if (result?.undo) {
                      undoManager.current?.push({
                        redo: [
                          getInputFocusOp(
                            `${sourceBlockId}-pp-toggle-doc-comp`
                          ),
                          { op: 'toggleMakeComponent', args: params },
                        ],
                        undo: [
                          getInputFocusOp(
                            `${sourceBlockId}-pp-toggle-doc-comp`
                          ),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          },
                        ],
                      })
                    }
                    va.track(
                      isComponent ? 'Component detached' : 'Component toggled'
                    )
                    ph.capture(
                      isComponent ? 'Component detached' : 'Component toggled'
                    )
                  }}
                  icon={
                    isComponent ? (
                      <ComponentNoneIcon className="mr-0.5" />
                    ) : (
                      <BlockIcon
                        block={{ type: 'component' }}
                        iconProps={{ className: 'mr-0.5' }}
                      />
                    )
                  }
                >
                  {isComponent ? 'Detach Component' : 'Make Component'}
                </Button>
              </SimpleTooltip>
            </PropertiesPanel.Group>
          )}
        </PropertiesPanel.ConfigureContainer>

        {isComponent && (
          <PropertiesPanel.CommonSearchConfigGroup
            block={block}
            parent={parent}
            path={path}
            label="Index block title"
            tooltipText={`If checked, this block's title will be indexed into BaseHub Search. If unchecked, this block's title will not be included in the index.`}
          />
        )}

        {isComponent && (
          <>
            <PropertiesPanel.Group title="Display info" titleClassName="!mb-3">
              <PropertiesPanel.TextOption
                name="Title"
                id={`el-${sourceBlockId}-pp-title`}
                placeholder={block.title}
                value={block.typeOptions.displayTitle}
                mutate={async (v) => {
                  const params: UpdateBlockParams = {
                    id: block.id,
                    data: { typeOptions: { displayTitle: v } },
                    instanceChildTarget: block.instanceChildTarget,
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-title`),
                        { op: 'updateBlock', args: params },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-title`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                      undo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-title`),
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-title`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                    })
                  }
                }}
              />
              <PropertiesPanel.TextOption
                multiLine
                maxLength={320}
                name="Description"
                id={`el-${sourceBlockId}-pp-description`}
                placeholder={'An optional description'}
                value={block.typeOptions.displayDescription}
                mutate={async (v) => {
                  const params: UpdateBlockParams = {
                    id: block.id,
                    data: { typeOptions: { displayDescription: v } },
                    instanceChildTarget: block.instanceChildTarget,
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-description`),
                        { op: 'updateBlock', args: params },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-description`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                      undo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-description`),
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-description`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                    })
                  }
                }}
              />
              <PropertiesPanel.ImageOrVideoOption
                name="Preview"
                id={`el-${sourceBlockId}-pp-preview`}
                value={block.typeOptions.displayImage ?? null}
                tooltipText="Preview of how the component looks in the actual website"
                mutate={async (v) => {
                  const params: UpdateBlockParams = {
                    id: block.id,
                    data: { typeOptions: { displayImage: v } },
                    instanceChildTarget: block.instanceChildTarget,
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-preview`),
                        { op: 'updateBlock', args: params },
                      ],
                      undo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-preview`),
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                }}
              />
            </PropertiesPanel.Group>
            {collectionsCount !== 0 && (
              <PropertiesPanel.Group
                title={`Collections (${collectionsCount})`}
              >
                {relations?.collections
                  .slice(0, !seeAllCollections ? 5 : undefined)
                  .map((collection) => (
                    <PropertiesPanel.LinkOption
                      key={collection.key}
                      label={
                        collection.value.title !== ''
                          ? collection.value.title
                          : 'Untitled'
                      }
                      icon={
                        <BlockIcon
                          block={{ type: 'list' }}
                          iconProps={{ width: 14, height: 14 }}
                        />
                      }
                      href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                        collection.value
                      )}`}
                    />
                  ))}
                {collectionsCount > 5 && (
                  <button
                    className="inline-flex items-center gap-1 text-xs max-w-max text-dark-faint"
                    name="see all collections"
                    onClick={() => setSeeAllCollections(!seeAllCollections)}
                  >
                    {seeAllCollections ? 'See less' : 'See all'}
                    <ChevronDownIcon
                      className={clsx({
                        'mt-px -rotate-180 transform': seeAllCollections,
                      })}
                    />
                  </button>
                )}
                {collectionsCount === 0 && (
                  <p className="text-xs text-dark-faint">
                    No collections linked to this component.
                  </p>
                )}
              </PropertiesPanel.Group>
            )}
            {instanceCount !== 0 && (
              <PropertiesPanel.Group title={`Instances (${instanceCount})`}>
                {relations?.instances
                  .slice(0, !seeAllInstances ? 5 : undefined)
                  .map((instance) => (
                    <PropertiesPanel.LinkOption
                      key={instance.key}
                      label={
                        instance.value.title !== ''
                          ? instance.value.title
                          : 'Untitled'
                      }
                      icon={
                        <BlockIcon
                          block={{ type: 'instance' }}
                          iconProps={{ width: 14, height: 14 }}
                        />
                      }
                      href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                        instance.value
                      )}`}
                    />
                  ))}
                {instanceCount > 5 && (
                  <button
                    className="inline-flex items-center gap-1 text-xs max-w-max text-dark-faint"
                    name="see all instances"
                    onClick={() => setSeeAllInstances(!seeAllInstances)}
                  >
                    {seeAllInstances ? 'See less' : 'See all'}
                    <ChevronDownIcon
                      className={clsx('', {
                        'mt-px -rotate-180 transform': seeAllInstances,
                      })}
                    />
                  </button>
                )}
                {instanceCount === 0 && (
                  <p className="text-xs text-dark-faint">
                    No instances linked to this component.
                  </p>
                )}
              </PropertiesPanel.Group>
            )}
            <ReferencedByGroup blockId={block.id} />
            {instancesInRichTextCount !== 0 && (
              <PropertiesPanel.Group
                title={`Instances in Rich Text (${instancesInRichTextCount})`}
              >
                {relations?.instancesInRichText
                  .slice(0, !seeAllRichTextInstances ? 5 : undefined)
                  .map((instance) => (
                    <PropertiesPanel.LinkOption
                      key={instance.key}
                      label={
                        instance.value.title !== ''
                          ? instance.value.title
                          : 'Untitled'
                      }
                      icon={
                        <BlockIcon
                          block={{ type: 'instance' }}
                          iconProps={{ width: 14, height: 14 }}
                        />
                      }
                      href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
                        instance.value
                      )}`}
                    />
                  ))}
                {instancesInRichTextCount > 5 && (
                  <button
                    className="inline-flex items-center gap-1 text-xs max-w-max text-dark-faint"
                    name="see all instances"
                    onClick={() =>
                      setSeeAllRichTextInstances(!seeAllRichTextInstances)
                    }
                  >
                    {seeAllRichTextInstances ? 'See less' : 'See all'}
                    <ChevronDownIcon
                      className={clsx('', {
                        'mt-px -rotate-180 transform': seeAllRichTextInstances,
                      })}
                    />
                  </button>
                )}
                {instancesInRichTextCount === 0 && (
                  <p className="text-xs text-dark-faint">
                    No instances linked to any rich text.
                  </p>
                )}
              </PropertiesPanel.Group>
            )}
          </>
        )}
      </PropertiesPanel.PropertiesContainer>

      {isComponent && typeof instanceCount === 'number' && (
        <DocActionDialog
          open={removeComponentToggleState}
          title="Are you sure you want to remove this component?"
          description={`There ${
            instanceCount === 1
              ? 'is 1 instance'
              : `are ${instanceCount} instances`
          } attached to it, which will be detatched with this action.`}
          handleClose={() => {
            setRemoveComponentToggleState(false)
          }}
          label="Remove"
          action={handleRemoveComponent}
        />
      )}
    </>
  )
}

export const HiddenTogglePropertiesPanel = ({
  block,
  path,
  parent,
}: {
  block: Block
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const inherits = treeManager
    ? shouldInheritHiddenState(
        block,
        path,
        (id) =>
          treeManager.getNode(id, {
            fallbackKey: 'sourceBlockId',
            required: true,
          }).value
      )
    : false

  const editorMode = useEditorMode()

  const shouldShowInheritedLabel = React.useMemo(() => {
    if (editorMode !== 'write:all' && !block.hidden) return false
    return inherits
  }, [block.hidden, editorMode, inherits])

  return (
    <PropertiesPanel.Group>
      <PropertiesPanel.BooleanOption
        disabled={inherits}
        forceEditable={isNonCrucialBlock(block, parent)}
        name="Hidden"
        id={`el-${sourceBlockId}-pp-hidden`}
        className="text-dark-bright"
        tooltipText="Hidden blocks will skip commit validation, and won't return from the API"
        value={block.hidden}
        mutate={async (v) => {
          const params: UpdateBlockParams = {
            id: block.id,
            data: { hidden: v },
            instanceChildTarget: block.instanceChildTarget,
            path,
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate.updateBlock(params)
          if (result?.undo) {
            undoManager.current?.push({
              redo: [
                getInputFocusOp(`el-${sourceBlockId}-pp-hidden`),
                { op: 'updateBlock', args: params },
              ],
              undo: [
                getInputFocusOp(`el-${sourceBlockId}-pp-hidden`),
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }
        }}
      />
      {shouldShowInheritedLabel && (
        <PropertiesPanel.Paragraph className="inline-flex items-center gap-2">
          <i>Inherits from parent block.</i>
        </PropertiesPanel.Paragraph>
      )}
    </PropertiesPanel.Group>
  )
}

export const FullViewDocumentPropertiesPanel = (props: Props) => {
  const selectedBlockId = useEditorStore((s) => s.selectedBlockId)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)

  const selectedBlock = selectedBlockId
    ? getNode?.(selectedBlockId, {
        fallbackKey: 'sourceBlockId',
      })?.value
    : null

  let isSelected = selectedBlockId === idToSourceBlockId(props.block)

  if (!props.strict && !isSelected) {
    isSelected = !selectedBlock || isCommentBlock(selectedBlock)
  }

  return (
    <DocumentSidebarPortal hidden={!isSelected}>
      <Root block={props.block} parentBlock={props.parent ?? null}>
        <PropertiesPanel.PropertiesContainer>
          <PropertiesPanel.BlockTitleGroup
            block={props.block}
            path={props.path}
          />
        </PropertiesPanel.PropertiesContainer>
        <DocumentPropertiesPanel {...props} />
      </Root>
    </DocumentSidebarPortal>
  )
}
